import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const UNASSIGNED = "__unassigned__";

export const EditFindingModal = ({ finding, teams, open, onClose, onSaved }) => {
  const [status, setStatus] = useState("Open");
  const [ownerTeam, setOwnerTeam] = useState(UNASSIGNED);
  const [targetDate, setTargetDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (finding) {
      setStatus(finding.status || "Open");
      setOwnerTeam(finding.owner_team || UNASSIGNED);
      setTargetDate(finding.target_date || "");
      setNotes(finding.notes || "");
    }
  }, [finding]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { status };
      if (ownerTeam !== UNASSIGNED) body.owner_team = ownerTeam;
      if (targetDate) body.target_date = targetDate;
      if (notes) body.notes = notes;
      await api.put(`/grc/findings/${finding.finding_id}`, body);
      toast.success("Finding updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update finding");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (status === "Closed" && finding?.status !== "Closed") {
      setConfirmClose(true);
      return;
    }
    save();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-finding-modal">
        <DialogHeader>
          <DialogTitle className="text-base text-[#003366]">Edit Finding</DialogTitle>
          <DialogDescription className="text-xs">Update status, owner, target date or notes.</DialogDescription>
        </DialogHeader>
        {finding && (
          <div className="space-y-4 py-1">
            <p className="text-xs text-slate-500 line-clamp-2">{finding.title}</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-finding-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="InProgress">In Progress</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                  <SelectItem value="AcceptedRisk">Accepted Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Owner Team</Label>
              <Select value={ownerTeam} onValueChange={setOwnerTeam}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-finding-owner-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.team_id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Target Date</Label>
              <Input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="h-9 text-sm"
                data-testid="edit-finding-target-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Remediation notes, context, blockers…"
                className="text-sm min-h-[80px]"
                data-testid="edit-finding-notes"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-finding-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSaveClick}
            disabled={saving}
            className="bg-[#008080] hover:bg-[#1a51db]"
            data-testid="edit-finding-save-button"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>

        <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
          <AlertDialogContent data-testid="close-finding-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-base">Close this finding?</AlertDialogTitle>
              <AlertDialogDescription className="text-xs">
                Closing the finding will also mark its linked remediation tasks as Done. This is recorded in the audit log.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="close-finding-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => { setConfirmClose(false); save(); }}
                className="bg-[#008080] hover:bg-[#1a51db]"
                data-testid="close-finding-confirm-button"
              >
                Close Finding
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
